import type { TradePolicyCatalogField } from "../types/tradePolicy";

/** Classifier v3 (`regime_v3.1_standard`) — primary UI picker set. */
export const MARKET_CONDITION_V3_VALUES = [
  "uptrend",
  "downtrend",
  "range",
  "choppy",
  "unknown",
] as const;

export type MarketConditionV3Value = (typeof MARKET_CONDITION_V3_VALUES)[number];

/**
 * Legacy v2 labels still accepted by the API / present on old policies and report
 * snapshots. Show when loading old data; never offer as the default picker set.
 */
export const MARKET_CONDITION_LEGACY_VALUES = [
  "accumulation",
  "expansion",
  "trend",
  "volatile",
  "range",
  "unknown",
] as const;

export type MarketConditionLegacyValue = (typeof MARKET_CONDITION_LEGACY_VALUES)[number];

export const MARKET_CONDITION_CATALOG_PATH = "regime.market_condition";

const V3_SET = new Set<string>(MARKET_CONDITION_V3_VALUES);
const LEGACY_ONLY_SET = new Set<string>(
  MARKET_CONDITION_LEGACY_VALUES.filter((v) => !V3_SET.has(v)),
);

/** Official meanings for tooltips / glossary (v3 + legacy display). */
export const MARKET_CONDITION_MEANINGS: Record<string, string> = {
  uptrend: "Higher highs / higher lows, positive follow-through",
  downtrend: "Lower highs / lower lows, negative follow-through",
  range: "Sideways, no sustained directional progress",
  choppy: "Sharp swings, poor follow-through / frequent reversals",
  unknown: "Not enough history / data to classify",
  // Legacy — display only when loading old trees / report rows
  trend: "The market was trending with follow-through (legacy label)",
  expansion: "Volatility and directional movement were elevated (legacy label)",
  accumulation: "Compressing / base-like range before a possible move (legacy label)",
  volatile: "Elevated volatility regime (legacy label)",
};

export function normalizeMarketConditionToken(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function isMarketConditionV3Value(value: string | null | undefined): boolean {
  return V3_SET.has(normalizeMarketConditionToken(value));
}

/** Legacy-only tokens (not also in v3). `range` / `unknown` overlap both sets. */
export function isMarketConditionLegacyOnlyValue(value: string | null | undefined): boolean {
  return LEGACY_ONLY_SET.has(normalizeMarketConditionToken(value));
}

export function formatMarketConditionLabel(value: string): string {
  const token = normalizeMarketConditionToken(value);
  if (!token) return "";
  return token.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Prefer filter-catalog `regime.market_condition.enum_values`.
 * Falls back to the official v3 five when catalog is missing or empty.
 */
export function resolveMarketConditionPickerValues(
  catalogFields: readonly TradePolicyCatalogField[] | null | undefined,
): string[] {
  const field = catalogFields?.find((f) => f.path === MARKET_CONDITION_CATALOG_PATH);
  const fromCatalog = (field?.enum_values ?? [])
    .map((v) => normalizeMarketConditionToken(v))
    .filter(Boolean);
  // Dedupe while preserving catalog order
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const v of fromCatalog) {
    if (seen.has(v)) continue;
    seen.add(v);
    ordered.push(v);
  }
  if (ordered.length > 0) return ordered;
  return [...MARKET_CONDITION_V3_VALUES];
}

/**
 * Build picker values: catalog/v3 primary set, plus the current value when it is a
 * legacy token still present on an opened slice / deep-link (display only).
 */
export function marketConditionPickerValuesForSelect(
  catalogFields: readonly TradePolicyCatalogField[] | null | undefined,
  currentValue?: string | null,
): string[] {
  const primary = resolveMarketConditionPickerValues(catalogFields);
  const current = normalizeMarketConditionToken(currentValue);
  if (!current || primary.includes(current)) return primary;
  if (isMarketConditionLegacyOnlyValue(current) || !V3_SET.has(current)) {
    return [...primary, current];
  }
  return primary;
}
